import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import PropTypes from "prop-types";

// Scales a content-driven question box down to fit the slide; passthrough (scale 1) when it fits.
export default function ExerciseAutoFit({ children }) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [naturalHeight, setNaturalHeight] = useState(null);

  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;

    // offsetHeight is unaffected by the transform, so it stays the true height.
    const naturalH = inner.offsetHeight;
    if (!naturalH) return;

    const slide = wrap.closest("#maincontent, #contained_maincontent, .sli");
    const box = wrap.closest(".wholebox");
    if (!slide || !box) {
      setScale(1);
      return;
    }

    const slideHeight = slide.clientHeight;
    const boxTop = box.offsetTop > 0 ? box.offsetTop : 0;
    const available = slideHeight - boxTop - 4;

    let next = available > 0 ? Math.min(1, available / naturalH) : 1;
    if (!isFinite(next) || next <= 0) next = 1;

    setScale((prev) => (Math.abs(prev - next) > 0.005 ? next : prev));
    setNaturalHeight(naturalH);
  }, []);

  useLayoutEffect(() => {
    measure();
    const inner = innerRef.current;
    if (!inner) return undefined;

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      ro.observe(inner);
    }
    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const scaled = scale < 1;

  return (
    <div
      ref={wrapRef}
      style={{
        width: "100%",
        // reserve scaled height so the box shrinks to match
        height: scaled && naturalHeight != null ? naturalHeight * scale : "auto",
      }}
    >
      <div
        ref={innerRef}
        style={{
          transform: scaled ? `scale(${scale})` : undefined,
          transformOrigin: "top center",
          width: "100%",
        }}
      >
        {children}
      </div>
    </div>
  );
}

ExerciseAutoFit.propTypes = {
  children: PropTypes.node,
};
